import {
    FSMNamedState,
    ObservableFiniteStateMachine,
    OMGroup as Group,
    OMQuery,
    OMReference,
    OMUniverse
} from "@apptivity-lab/firmament-node-sdk"

import { AffAttendeeProfile } from "@firmament-packages/aff"
import { CnxConsumerProfile, CnxMeeting } from "@firmament-packages/connectx"

import "../firmament-packages/aff/extensions/AffAttendeeProfileExtensions"

declare namespace AffAttendeeProfileMenuStateMachine {
    export interface Start extends FSMNamedState {
        name: "start"
    }

    export interface Loading extends FSMNamedState {
        name: "loading"
        profileId: OMReference<AffAttendeeProfile>
    }

    export interface Presenting extends FSMNamedState {
        name: "presenting"
        profile: AffAttendeeProfile
    }

    export interface ShowingError extends FSMNamedState {
        name: "showingError"
        error: Error
        profile?: AffAttendeeProfile
    }

    export interface LoggingOut extends FSMNamedState {
        name: "loggingOut"
    }

    export interface Completed extends FSMNamedState {
        name: "completed"
    }

    export type State = Start |
        Loading |
        Presenting |
        ShowingError |
        LoggingOut |
        Completed
}

export class AffAttendeeProfileMenuStateMachine extends ObservableFiniteStateMachine<AffAttendeeProfileMenuStateMachine.State> {
    constructor() {
        super({ name: "start" })
    }

    async load(id: OMReference<AffAttendeeProfile>) {
        switch (this.state.name) {
            case "start":
            case "presenting":
                break
            default:
                return
        }

        try {
            this.state = { name: "loading", profileId: id }

            const profile = await id.load(
                AffAttendeeProfile.query()
                    .include("photo")
                    .include("sessionWallets")
            )

            await profile.loadActiveSubscriptions()

            const meetingsQuery = new OMQuery(CnxMeeting, "meetings", new OMReference(CnxConsumerProfile, profile))
                .filter("meetingStatus", "notEquals", "FAILED")
                .filter("scheduledEndAt", "lessThan", (new Date()).toISOString() as any)
                .limit(0)

            await meetingsQuery.execute()

            profile.meetingCount = meetingsQuery.meta.count
            OMUniverse.shared.touch(profile)

            this.success(profile)
        } catch (error: unknown) {
            if (error instanceof Error) {
                this.fail(error)
            }
        }
    }

    async logout() {
        this.state = { name: "loggingOut" }

        try {
            await OMUniverse.shared.logout()
        } finally {
            this.state = { name: "completed" }
        }
    }

    private success(profile: AffAttendeeProfile) {
        switch (this.state.name) {
            case "start":
            case "loading":
            case "presenting":
                this.state = { name: "presenting", profile }
                break
            default:
                return
        }
    }

    private fail(error: Error) {
        switch (this.state.name) {
            case "loading":
                this.state = { name: "showingError", error, profile: this.state.profileId.actualObject }
                break
            default:
                return
        }
    }
}
